'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import styles from './RingProUsCost.module.css';

/* Founder figures (checked Oct 23 2026): typical subscription ring is $399
   plus $69.99/yr membership, billed yearly up front. Ring PRO's price comes
   from the store. */
const OTHER_DEVICE = 399;
const OTHER_YEARLY = 69.99;
const YEARS = [1, 2, 3] as const;

const otherAfter = (years: number) => OTHER_DEVICE + OTHER_YEARLY * years;
const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

type Props = {
  /** Ring PRO base price as a number, e.g. 479. */
  price: number;
};

/**
 * "Do the math." — Ring PRO against a subscription ring over 1–3 years.
 * Centred header in the fit section's type, three small year pills, one
 * plain headline, then two slim tracks: Ring PRO as one blue bar, the other
 * ring as its device plus one segment per membership year. Closes on the
 * blue buy CTA.
 */
export const RingProUsCost = ({ price }: Props) => {
  const t = useTranslations('homepage');
  const k = (key: string) => `homepage.ringProUs.cost.${key}`;
  const [years, setYears] = useState<number>(3);

  // Scale to the longest bar at 3 years so switching years grows the track.
  const scaleMax = Math.max(price, otherAfter(3));
  const pct = (v: number) => `${(v / scaleMax) * 100}%`;
  const diff = otherAfter(years) - price;

  return (
    <section
      className={styles.section}
      aria-labelledby="ring-pro-us-cost-title"
    >
      <div className={styles.inner}>
        <header className={styles.head}>
          <h2 id="ring-pro-us-cost-title" className={styles.title}>
            {t(k('title'))}
          </h2>
          <p className={styles.sub}>{t(k('sub'))}</p>

          <div
            className={styles.years}
            role="group"
            aria-label={t(k('aria.years'))}
          >
            {YEARS.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={years === n}
                className={styles.yearPill}
                onClick={() => setYears(n)}
              >
                {t(k('yearsN'), { count: n })}
              </button>
            ))}
          </div>
        </header>

        <p className={styles.headline} aria-live="polite">
          {diff > 0
            ? t(k('saveHeadline'), { amount: usd(diff), count: years })
            : t(k('evenHeadline'), { amount: usd(-diff) })}
        </p>

        <ul className={styles.rows}>
          <li className={styles.row}>
            <span className={styles.rowHead}>
              <span className={styles.label}>
                {t(k('pro'))}
                <span className={styles.note}>{t(k('proNote'))}</span>
              </span>
              <span className={`${styles.total} ${styles.totalPro}`}>
                {usd(price)}
              </span>
            </span>
            <span className={styles.track} aria-hidden>
              <span
                className={`${styles.seg1} ${styles.pro}`}
                style={{ width: pct(price) }}
              />
            </span>
          </li>

          <li className={styles.row}>
            <span className={styles.rowHead}>
              <span className={styles.label}>
                {t(k('other'))}
                <span className={styles.note}>{t(k('otherNote'))}</span>
              </span>
              <span className={styles.total}>{usd(otherAfter(years))}</span>
            </span>
            <span className={styles.track} aria-hidden>
              <span
                className={`${styles.seg1} ${styles.device}`}
                style={{ width: pct(OTHER_DEVICE) }}
              />
              {YEARS.map((n) => (
                <span
                  key={n}
                  className={`${styles.seg1} ${styles.member}`}
                  data-on={n <= years ? 'true' : undefined}
                  style={{ width: n <= years ? pct(OTHER_YEARLY) : '0%' }}
                />
              ))}
            </span>
          </li>
        </ul>

        <p className={styles.legend} aria-hidden>
          <span className={styles.keyDevice} />
          {t(k('legendDevice'))}
          <span className={styles.keyMember} />
          {t(k('legendMember'))}
        </p>

        <p className={styles.fine}>{t(k('fine'))}</p>

        <div className={styles.ctaWrap}>
          <CustomLink
            href="/ring-pro/buy"
            className={styles.cta}
            data-buttontype="ring_pro_us_cost_buy_button"
          >
            {t('homepage.ringProUs.cta.buy')}
          </CustomLink>
        </div>
      </div>
    </section>
  );
};

export default RingProUsCost;
